import { updateAppAlert } from '@labrav/common'
import { AxiosError, AxiosResponse } from 'axios'
import {
  CancelPrivateOfferFailureMessage,
  CancelPrivateOfferProgressMessage,
  DeletePrivateOfferFailureMessage,
  DeletePrivateOfferSuccessMessage,
  PatchPrivateOfferGeneralMessage,
  PatchPrivateOffersFailureMessage,
  PatchPrivateOffersSuccessMessage,
  resendPrivateOfferSuccess,
  sendReminderToBuyerSuccess,
  clonePrivateOfferSuccess,
  ExtendPrivateOfferExpiryDateMessage,
  DeactivationPrivateOfferProgressMessage,
} from '../../common/utils/messagesContants'
import { AnyAction } from 'redux'
import type { AppDispatch, RootState } from '../../store/types'
import {
  patchPrivateOffersData,
  sendPrivateOfferReminder,
} from '../../api/marketplace'
import {
  cloneMicrosoftPrivateOfferAPI,
  resetMicrosoftPrivateOfferAPI,
} from '../../api/azure'
import { camelize, snakeize } from 'casing'
import { isEmpty } from 'lodash'
import {
  getErrorMessages,
  RequestFailureMessage,
  errorLogger,
  LoadingTypes,
  startLoading,
  stopLoading,
} from '@labrav/common'
import {
  OfferStatus,
  PrivateOfferActionTypes,
  type PrivateOffer,
} from './types'
import type { CloudMarketplace, Product } from '../productsListing/types'
import { getProducts } from '../productsListing/action'
import {
  getPayloadDataBasedOnCloud,
  postOfferBasedOnCloud,
} from './privateOfferCreation/action'
import { storeClonePurchaseTerms } from './privateOfferCreation/actionTypes'
import type { ClonePurchaseTerms } from './privateOfferCreation/utils/microsoftPurchaseTermsUtil'
import { defaultCrmType } from '../../utils/constants'
import { isAzureContainerProduct } from '../../components/MicrosoftFormComponent/ContainerPlan/containerPlanUtils'
import { findProductByProductRef } from '../../utils/privateOfferOrResaleHelperFunctions'
import {
  getPrivateOfferAPIBasedOnCloud,
  getPrivateOffersAPIBasedOnCloud,
  patchOfferBasedOnCloud,
  deleteOfferBasedOnCloud,
  processPrivateOffers,
  processSinglePrivateOffer,
  getOfferForCloneOffer,
  resolveProductIdForPrivateOfferFetch,
  getIsContainerProductFromState,
} from './utils'

export { processPrivateOffers, processSinglePrivateOffer }

export const getPrivateOffers =
  ({
    partnerId,
    cloudMarketplace,
    pageSize,
    pageNumber,
    productId,
    crmOpportunityId,
    expiringInDays,
    offerStatus,
    input,
    selectedInput,
    sectionName,
  }: {
    partnerId: string
    cloudMarketplace: CloudMarketplace
    pageSize?: number
    pageNumber?: number
    productId?: string
    crmOpportunityId?: string
    expiringInDays?: string
    offerStatus?: string[]
    input?: string
    selectedInput?: string
    sectionName?: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await getPrivateOffersAPIBasedOnCloud(
        partnerId,
        cloudMarketplace,
        pageSize,
        pageNumber,
        productId,
        crmOpportunityId,
        expiringInDays,
        offerStatus,
        input,
        selectedInput,
        sectionName
      )

      const privateOffers = await processPrivateOffers({
        cloudMarketplace,
        response,
      })
      await dispatch(setPrivateOffersData(camelize(privateOffers)))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const searchPrivateOfferById =
  ({
    partnerId,
    privateOfferId,
    cloudMarketplace,
    productId,
    crmOpportunityId,
  }: {
    partnerId: string
    privateOfferId: string
    cloudMarketplace: CloudMarketplace
    productId?: string
    crmOpportunityId?: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const products =
        getState().productsListing[cloudMarketplace]?.products?.rows || []
      const selectedProduct = findProductByProductRef(products, productId)
      const response = await getPrivateOfferAPIBasedOnCloud({
        partnerId,
        cloud: cloudMarketplace,
        privateOfferId,
        crmOpportunityId,
        productId: selectedProduct?.productId || productId,
        product: selectedProduct,
        isContainerProduct: isAzureContainerProduct(selectedProduct),
      })
      if (isEmpty(response?.data)) {
        dispatch(clearPrivateOffers())
        return
      }
      const offer = camelize(
        await processSinglePrivateOffer({
          cloudMarketplace,
          response,
          selectedProduct,
          getState,
        })
      ) as Record<string, unknown>
      dispatch(
        setPrivateOffersData({
          rows: [
            {
              ...offer,
              privateOfferId: offer.privateOfferId || privateOfferId,
            },
          ],
          pageNumber: 1,
          pageSize: 1,
          totalCount: 1,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      if (errorResponse?.status !== 404) {
        dispatch(
          updateAppAlert({
            message: getErrorMessages([RequestFailureMessage])(errorResponse),
            messageType: 'ERROR',
            autoClose: true,
          })
        )
        errorLogger({ globalState: getState() })(
          error as Error,
          errorResponse?.data?.track_id
        )
      }
      dispatch(clearPrivateOffers())
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const setPrivateOffersData = (data: Record<string, unknown>) =>
  ({
    type: PrivateOfferActionTypes.SET_PRIVATE_OFFERS_DATA as PrivateOfferActionTypes.SET_PRIVATE_OFFERS_DATA,
    payload: {
      data: data.rows,
      pageNumber: data.pageNumber ?? 0,
      pageSize: data.pageSize ?? 10,
      totalCount: data.totalCount ?? 0,
      sortBy: data.sortBy ?? '',
      sortOrder: data.sortOrder ?? 'desc',
      links: data.links ?? {},
      freeTierInfo: data.freeTierInfo,
    },
  }) as unknown as AnyAction

export const setPageNumber = (pageNumber: number) =>
  ({
    type: PrivateOfferActionTypes.PRIVATE_OFFERS_SET_PAGE_NUMBER,
    payload: pageNumber,
  }) as unknown as AnyAction

export const updatePageSize =
  ({
    partnerId,
    cloudMarketplace,
    pageSize,
    productId,
    crmOpportunityId,
  }: {
    partnerId: string
    cloudMarketplace: CloudMarketplace
    pageSize: number
    productId?: string
    crmOpportunityId?: string
  }) =>
  async (dispatch: AppDispatch) => {
    await dispatch(setPageSize(pageSize))
    await dispatch(
      getPrivateOffers({
        partnerId,
        cloudMarketplace,
        pageSize,
        pageNumber: 1,
        productId,
        crmOpportunityId,
      }) as unknown as AnyAction
    )
  }

export const setPageSize = (pageSize: number) => ({
  type: PrivateOfferActionTypes.PRIVATE_OFFERS_SET_LIMIT,
  payload: pageSize,
})

export const updateOffer = (
  key: string,
  value: string,
  privateOfferId: string
) =>
  ({
    type: PrivateOfferActionTypes.UPDATE_PRIVATE_OFFERS_DATA as PrivateOfferActionTypes.UPDATE_PRIVATE_OFFERS_DATA,
    payload: {
      privateOfferId: privateOfferId,
      key: key,
      value: value,
    },
  }) as unknown as AnyAction

export const patchOfferData =
  (partnerId: string, privateOfferId: string, crmOpportunityId?: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const privateOffers = getState().privateOffers?.privateOffers || {}
      const { awsOfferId, cloudOfferUrl } = privateOffers[privateOfferId]
      await patchPrivateOffersData(
        partnerId,
        privateOfferId,
        snakeize({
          crmOpportunityId,
          awsOfferId: awsOfferId ?? '',
          cloudOfferUrl: cloudOfferUrl ?? '',
        })
      )
      dispatch(
        updateAppAlert({
          message: PatchPrivateOffersSuccessMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([PatchPrivateOffersFailureMessage])(
            errorResponse
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const clearPrivateOffers = () =>
  ({
    type: PrivateOfferActionTypes.CLEAR_PRIVATE_OFFERS_DATA,
  }) as unknown as AnyAction

export const getCurrentPrivateOffer =
  (
    partnerId: string,
    privateOfferId: string,
    cloudMarketplace: CloudMarketplace,
    productId?: string,
    crmOpportunityId?: string,
    isContainerProductOverride?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    await dispatch(clearPrivateOffers())
    const readSelectedProduct = () => {
      const products =
        getState().productsListing[cloudMarketplace]?.products?.rows || []
      return findProductByProductRef(products, productId)
    }
    let selectedProduct = readSelectedProduct()
    if (
      cloudMarketplace === 'GCP' &&
      productId &&
      isEmpty(selectedProduct?.plans)
    ) {
      await dispatch(getProducts(partnerId, cloudMarketplace, false))
      selectedProduct = readSelectedProduct()
    }
    const resolvedProduct = selectedProduct || ({} as Product)
    const effectiveProductId = resolvedProduct.productId || productId
    const isContainerProduct =
      isContainerProductOverride !== undefined
        ? isContainerProductOverride
        : isAzureContainerProduct(resolvedProduct)
    try {
      const response = await getPrivateOfferAPIBasedOnCloud({
        partnerId,
        cloud: cloudMarketplace,
        privateOfferId,
        crmOpportunityId,
        productId: effectiveProductId,
        product: resolvedProduct,
        isContainerProduct,
      })
      const privateOffer = await processSinglePrivateOffer({
        cloudMarketplace,
        response,
        selectedProduct: resolvedProduct,
        getState,
      })
      const normalizedOffer = camelize(privateOffer) as Record<string, unknown>
      dispatch(
        setCurrentPrivateOffer({
          ...normalizedOffer,
          privateOfferId:
            (normalizedOffer.privateOfferId as string) || privateOfferId,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const setCurrentPrivateOffer = (data: Record<string, unknown>) =>
  ({
    type: PrivateOfferActionTypes.SET_CURRENT_PRIVATE_OFFER,
    payload: data,
  }) as unknown as AnyAction

export const clearCurrentPrivateOffers = () =>
  ({
    type: PrivateOfferActionTypes.CLEAR_CURRENT_PRIVATE_OFFER,
  }) as unknown as AnyAction

export const updatePrivateOfferForUser =
  (
    privateOfferId: string,
    data: Record<string, unknown>,
    crmOpportunityId?: string,
    editOffer?: boolean,
    callBack?: () => void,
    cloudMarketplace?: CloudMarketplace,
    partnerId?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const cloudMarketplaceFromState =
      getState().productListingFilters.cloudMarketplace
    const cloud = cloudMarketplace || cloudMarketplaceFromState
    await dispatch(startLoading(LoadingTypes.GENERAL))
    const { offerStatus, operation } = data
    const isCallForCancel =
      offerStatus === OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS ||
      operation === 'UPDATE'
    try {
      const selectedAccountIds = getState().accountSelection?.selectedAccountIds
      const accountIdFromState = selectedAccountIds?.flyout?.[cloud] || ''
      const partnerIdToUse =
        partnerId ||
        accountIdFromState ||
        getState().AccountData.user?.partnerData?.partnerId ||
        ''
      const isContainerProduct =
        getIsContainerProductFromState(getState, cloud) && !isCallForCancel
      await patchOfferBasedOnCloud(
        cloud,
        partnerIdToUse,
        privateOfferId,
        snakeize(data),
        crmOpportunityId,
        editOffer,
        isContainerProduct
      )
      const successMessage = isCallForCancel
        ? cloudMarketplace === 'AZURE'
          ? DeactivationPrivateOfferProgressMessage
          : CancelPrivateOfferProgressMessage
        : PatchPrivateOfferGeneralMessage
      dispatch(
        updateAppAlert({
          message: successMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      if (!editOffer) {
        const resolvedProductId = resolveProductIdForPrivateOfferFetch(
          getState,
          cloud,
          privateOfferId
        )
        await dispatch(
          getCurrentPrivateOffer(
            partnerIdToUse,
            privateOfferId,
            cloud,
            resolvedProductId,
            crmOpportunityId,
            isCallForCancel ? false : undefined
          ) as unknown as AnyAction
        )
      }
    } catch (error: any) {
      const errorMessage =
        offerStatus === OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS
          ? CancelPrivateOfferFailureMessage
          : RequestFailureMessage
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([errorMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (callBack) {
        await callBack()
      }
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const updatePrivateOfferExpiryDate =
  (
    privateOfferId: string,
    data: Record<string, unknown>,
    crmOpportunityId?: string,
    callBack?: () => void,
    partnerId?: string,
    cloudMarketplace?: CloudMarketplace
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const cloud =
      cloudMarketplace || getState().productListingFilters.cloudMarketplace
    await dispatch(startLoading(LoadingTypes.UPDATE_FLYOUT_OFFER))
    try {
      const partnerIdToUse =
        partnerId || getState().AccountData.user?.partnerData?.partnerId || ''
      await patchOfferBasedOnCloud(
        cloud,
        partnerIdToUse,
        privateOfferId,
        snakeize(data),
        crmOpportunityId,
        undefined,
        getIsContainerProductFromState(getState, cloud)
      )
      const successMessage = ExtendPrivateOfferExpiryDateMessage
      dispatch(
        updateAppAlert({
          message: successMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (callBack) {
        await callBack()
      }
      await dispatch(stopLoading(LoadingTypes.UPDATE_FLYOUT_OFFER))
    }
  }

export const deleteCurrentPrivateOffer =
  (
    privateOfferId: string,
    callBack?: () => void,
    partnerId?: string,
    cloudMarketplace?: CloudMarketplace
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const cloud =
      cloudMarketplace || getState().productListingFilters.cloudMarketplace
    const { pageSize } = getState().privateOffers || {
      pageNumber: 0,
      pageSize: 10,
    }
    await dispatch(startLoading(LoadingTypes.DELETE_OFFER))
    try {
      const partnerIdToUse =
        partnerId || getState().AccountData.user?.partnerData?.partnerId || ''
      await deleteOfferBasedOnCloud(cloud, partnerIdToUse, privateOfferId)
      await dispatch(clearPrivateOffers())
      await dispatch(
        getPrivateOffers({
          partnerId: partnerIdToUse,
          cloudMarketplace: cloud,
          pageSize,
          pageNumber: 1,
        }) as unknown as AnyAction
      )
      dispatch(
        updateAppAlert({
          message: DeletePrivateOfferSuccessMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([DeletePrivateOfferFailureMessage])(
            errorResponse
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (callBack) {
        await callBack()
      }
      await dispatch(stopLoading(LoadingTypes.DELETE_OFFER))
    }
  }

export const sendReminder =
  ({
    privateOfferId,
    data,
    callBack,
    partnerId,
    cloudMarketplace,
  }: {
    privateOfferId: string
    data: Record<string, unknown>
    callBack?: () => void
    partnerId?: string
    cloudMarketplace?: CloudMarketplace
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const cloud =
      cloudMarketplace || getState().productListingFilters.cloudMarketplace
    await dispatch(startLoading(LoadingTypes.UPDATE_FLYOUT_OFFER))

    try {
      const partnerIdToUse =
        partnerId || getState().AccountData.user?.partnerData?.partnerId || ''
      await sendPrivateOfferReminder(
        partnerIdToUse,
        privateOfferId,
        snakeize({ ...data })
      )
      dispatch(
        updateAppAlert({
          message: sendReminderToBuyerSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (callBack) {
        await callBack()
      }
      await dispatch(stopLoading(LoadingTypes.UPDATE_FLYOUT_OFFER))
    }
  }

export const resendOffer =
  ({
    privateOfferId,
    data,
    callBack,
    partnerId,
    cloudMarketplace,
  }: {
    privateOfferId: string
    data: Record<string, unknown>
    callBack?: () => void
    partnerId?: string
    cloudMarketplace?: CloudMarketplace
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const cloud =
      cloudMarketplace || getState().productListingFilters.cloudMarketplace
    await dispatch(startLoading(LoadingTypes.UPDATE_FLYOUT_OFFER))

    try {
      const partnerIdToUse =
        partnerId || getState().AccountData.user?.partnerData?.partnerId || ''
      await sendPrivateOfferReminder(
        partnerIdToUse,
        privateOfferId,
        snakeize({ ...data }),
        true
      )
      dispatch(
        updateAppAlert({
          message: resendPrivateOfferSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (callBack) {
        await callBack()
      }
      await dispatch(stopLoading(LoadingTypes.UPDATE_FLYOUT_OFFER))
    }
  }

export const clonePrivateOffer =
  ({
    partnerId,
    cloudMarketplace,
    privateOffer,
    productId,
    callBack,
    crmObjectLink,
    crmOpportunityId,
  }: {
    partnerId: string
    cloudMarketplace: CloudMarketplace
    privateOffer: PrivateOffer
    productId: string
    crmObjectLink?: string
    callBack?: ({ offerId }: { offerId?: string }) => void
    crmOpportunityId?: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.UPDATE_FLYOUT_OFFER))

    try {
      const selectedProduct =
        getState().productsListing[cloudMarketplace]?.products?.rows.find(
          (product: Product) => product.productId === productId
        ) || ({} as Product)
      const productType = selectedProduct.productType
      const offerPayloadForCloneData = getOfferForCloneOffer({
        privateOffer,
        productType,
      })
      const { installments } = offerPayloadForCloneData
      const { email, firstName } = getState().userProfile.userProfile

      const creatorData = { creator_name: firstName, creator_email: email }

      const privateOfferToBeSent = getPayloadDataBasedOnCloud({
        cloud: cloudMarketplace,
        privateOfferData: offerPayloadForCloneData,
        getState,
        saveAsDraft: true,
        crmOpportunityId,
        selectedProduct,
      })

      let crmMetadata = {} as Record<string, unknown>
      if (crmOpportunityId && crmObjectLink) {
        crmMetadata = {
          crmType: defaultCrmType,
          crmObjectLink,
        }
      }
      const isContainerProduct = isAzureContainerProduct(selectedProduct)
      const payload = {
        partnerId,
        data: snakeize({
          ...privateOfferToBeSent,
          ...(!isContainerProduct && crmOpportunityId ? { crmMetadata } : {}),
          ...(cloudMarketplace === 'AWS'
            ? { installment_info: installments }
            : {}),
          ...(!isContainerProduct ? { creatorData } : {}),
        }),
        saveAsDraft: true,
        isContainerProduct,
      }
      const response = await postOfferBasedOnCloud(cloudMarketplace, payload)
      if (response && response.data && callBack) {
        callBack({ offerId: camelize(response.data).privateOfferId })
      }
      dispatch(
        updateAppAlert({
          message: clonePrivateOfferSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.UPDATE_FLYOUT_OFFER))
    }
  }

export const cloneMicrosoftPrivateOffer =
  ({
    partnerId,
    cloneId,
    originalOfferData,
    callBack,
  }: {
    partnerId: string
    cloneId: string
    originalOfferData?: ClonePurchaseTerms
    callBack?: ({ offerId }: { offerId?: string }) => void
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.UPDATE_FLYOUT_OFFER))

    try {
      const partnerIdToUse =
        partnerId || getState().AccountData.user?.partnerData?.partnerId || ''

      if (originalOfferData) {
        await dispatch(storeClonePurchaseTerms(originalOfferData) as AnyAction)
      }

      const response = await cloneMicrosoftPrivateOfferAPI({
        partnerId: partnerIdToUse,
        cloneId,
      })
      if (response && response.data && callBack) {
        callBack({ offerId: camelize(response.data).privateOfferId })
      }
      dispatch(
        updateAppAlert({
          message: clonePrivateOfferSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.UPDATE_FLYOUT_OFFER))
    }
  }

export const resetMicrosoftPrivateOffer =
  ({
    offerId,
    callBack,
    partnerId,
  }: {
    offerId: string
    callBack?: () => void
    partnerId?: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.UPDATE_FLYOUT_OFFER))

    try {
      const partnerIdToUse =
        partnerId || getState().AccountData.user?.partnerData?.partnerId || ''

      await resetMicrosoftPrivateOfferAPI({
        partnerId: partnerIdToUse,
        offerId,
      })
      if (callBack) {
        await callBack()
      }
      dispatch(
        updateAppAlert({
          message: PatchPrivateOfferGeneralMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.UPDATE_FLYOUT_OFFER))
    }
  }

export const deleteCurrentCppoOffer =
  (
    privateOfferId: string,
    callBack?: () => void,
    partnerId?: string,
    cloudMarketplace?: CloudMarketplace
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const cloud =
      cloudMarketplace || getState().productListingFilters.cloudMarketplace
    await dispatch(startLoading(LoadingTypes.DELETE_OFFER))
    try {
      const partnerIdToUse =
        partnerId || getState().AccountData.user?.partnerData?.partnerId || ''
      await deleteOfferBasedOnCloud(cloud, partnerIdToUse, privateOfferId)
      dispatch(
        updateAppAlert({
          message: DeletePrivateOfferSuccessMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([DeletePrivateOfferFailureMessage])(
            errorResponse
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (callBack) {
        await callBack()
      }
      await dispatch(stopLoading(LoadingTypes.DELETE_OFFER))
    }
  }
